import { getCurrentIdToken } from './auth';
import { getKioskDeviceToken } from './kioskToken';
import { getMemberToken } from './memberToken';

const BASE_URL = import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${status}`,
    );
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: 'admin' | 'kiosk' | 'member' | 'none' } = {},
): Promise<T> {
  const { auth = 'none', headers, ...rest } = init;
  const finalHeaders = new Headers(headers);
  if (rest.body && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  if (auth === 'admin') {
    const token = await getCurrentIdToken();
    if (!token) throw new ApiError(401, { error: 'not authenticated' });
    finalHeaders.set('Authorization', `Bearer ${token}`);
  } else if (auth === 'kiosk') {
    const token = getKioskDeviceToken();
    if (!token) throw new ApiError(401, { error: 'kiosk not bound' });
    finalHeaders.set('X-Kiosk-Device-Token', token);
  } else if (auth === 'member') {
    // v0.15 (ADR-0008): the member's own session. A missing token is an
    // ordinary state here, not a fault -- iOS can evict storage between two
    // monthly uses (PRD R-2) -- so it surfaces as a plain 401 the member
    // screen turns back into the code prompt.
    const token = getMemberToken();
    if (!token) throw new ApiError(401, { error: 'not identified' });
    finalHeaders.set('X-Member-Token', token);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...rest, headers: finalHeaders });
  } catch {
    throw new ApiError(0, { error: 'Netzwerkfehler -- keine Verbindung' });
  }

  const contentType = res.headers.get('Content-Type') ?? '';
  const body = contentType.includes('application/json')
    ? await res.json().catch(() => undefined)
    : await res.text();
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export const api = {
  get: <T>(path: string, auth: 'admin' | 'kiosk' | 'member' | 'none' = 'admin') =>
    request<T>(path, { method: 'GET', auth }),
  post: <T>(path: string, body?: unknown, auth: 'admin' | 'kiosk' | 'member' | 'none' = 'admin') =>
    request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      auth,
    }),
  put: <T>(path: string, body?: unknown, auth: 'admin' | 'kiosk' | 'member' | 'none' = 'admin') =>
    request<T>(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      auth,
    }),
  del: <T>(path: string, auth: 'admin' | 'kiosk' | 'member' | 'none' = 'admin', body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      auth,
    }),
};

export function csvDownloadUrl(path: string): Promise<string> {
  return getCurrentIdToken().then((token) => {
    // CSV endpoints are fetched with auth then turned into a Blob URL,
    // since a plain <a href> can't carry an Authorization header.
    return fetch(`${BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => URL.createObjectURL(blob));
  });
}
